import React, { useState, useEffect, useRef } from 'react';
import { Worker, supabase } from '../../lib/supabase';
import { X, Users, Mail, Phone, Calendar, Award, Plus, Minus, DollarSign, FileText, Upload, Trash2, ExternalLink, Loader } from 'lucide-react';

interface WorkerFormProps {
  worker?: Worker | null;
  onSubmit: (workerData: Partial<Worker>) => Promise<void>;
  onClose: () => void;
  loading?: boolean;
}

interface TrainingRecord {
  id: string;
  title: string;
  document_type: string;
  document_url: string;
  uploaded_at: string;
}

const DOCUMENT_TYPES = [
  'Certificate',
  'Licence',
  'Ticket',
  'Medical / Fitness',
  'Other',
] as const;

const TYPE_COLORS: Record<string, string> = {
  'Certificate': 'bg-blue-100 text-blue-700',
  'Licence': 'bg-green-100 text-green-700',
  'Ticket': 'bg-amber-100 text-amber-700',
  'Medical / Fitness': 'bg-rose-100 text-rose-700',
  'Other': 'bg-gray-100 text-gray-600',
};

export const WorkerForm: React.FC<WorkerFormProps> = ({ worker, onSubmit, onClose, loading = false }) => {
  const [availableCertifications, setAvailableCertifications] = useState<string[]>([]);
  const [loadingCertifications, setLoadingCertifications] = useState(true);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [customRole, setCustomRole] = useState('');
  const [selectedCertifications, setSelectedCertifications] = useState<string[]>(
    worker?.certifications || []
  );
  const [customCertification, setCustomCertification] = useState('');

  const [formData, setFormData] = useState({
    name: worker?.name || '',
    role: worker?.role || '',
    email: worker?.email || '',
    phone: worker?.phone || '',
    start_date: worker?.start_date || '',
    status: worker?.status || 'active',
    whitecard_number: worker?.whitecard_number || '',
    requires_health_monitoring: worker?.requires_health_monitoring || false,
    fit_testing_not_applicable: worker?.fit_testing_not_applicable || false,
    pin: worker?.pin || '',
    hourly_pay_rate: worker?.hourly_pay_rate?.toString() || '',
    overtime_pay_rate: worker?.overtime_pay_rate?.toString() || ''
  });

  // Training records state (only for editing)
  const [trainingRecords, setTrainingRecords] = useState<TrainingRecord[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [newRecordTitle, setNewRecordTitle] = useState('');
  const [newRecordType, setNewRecordType] = useState<string>(DOCUMENT_TYPES[0]);
  const [newRecordFile, setNewRecordFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchCertifications();
    fetchRoles();
    if (worker?.id) fetchTrainingRecords();
  }, []);

  const fetchRoles = async () => {
    try {
      const { data, error } = await supabase
        .from('workers')
        .select('role')
        .not('role', 'is', null);

      if (error) throw error;

      const roles = Array.from(
        new Set(data?.map(w => w.role?.trim()).filter(Boolean) as string[])
      ).sort();
      setAvailableRoles(roles);

      if (!formData.role && roles.length > 0) {
        setFormData(prev => ({ ...prev, role: roles[0] }));
      }
    } catch {
      setAvailableRoles(['worker', 'supervisor', 'foreman', 'safety_officer', 'operator', 'asbestos_specialist', 'contractor', 'visitor']);
    }
  };

  const fetchCertifications = async () => {
    try {
      const { data, error } = await supabase
        .from('workers')
        .select('certifications')
        .not('certifications', 'is', null);

      if (error) throw error;

      const allCertifications = new Set<string>();
      data?.forEach(w => {
        if (w.certifications && Array.isArray(w.certifications)) {
          w.certifications.forEach((cert: string) => {
            if (cert && cert.trim()) allCertifications.add(cert.trim());
          });
        }
      });
      setAvailableCertifications(Array.from(allCertifications).sort());
    } catch {
      setAvailableCertifications([
        'White Card', 'First Aid Certificate', 'Asbestos Awareness',
        'Working at Heights', 'Confined Space', 'Manual Handling',
        'Forklift License', 'Crane License', 'Scaffolding License'
      ]);
    } finally {
      setLoadingCertifications(false);
    }
  };

  const fetchTrainingRecords = async () => {
    if (!worker?.id) return;
    setLoadingRecords(true);
    try {
      const { data, error } = await supabase
        .from('worker_training_records')
        .select('*')
        .eq('worker_id', worker.id)
        .order('uploaded_at', { ascending: false });
      if (error) throw error;
      setTrainingRecords(data || []);
    } catch (err) {
      console.error('Error fetching training records:', err);
    } finally {
      setLoadingRecords(false);
    }
  };

  const handleUploadRecord = async () => {
    if (!worker?.id || !newRecordTitle.trim() || !newRecordFile) return;
    setUploading(true);
    setUploadError('');
    try {
      const ext = newRecordFile.name.split('.').pop();
      const path = `${worker.id}/${Date.now()}_${newRecordFile.name}`;

      const { error: storageError } = await supabase.storage
        .from('training-documents')
        .upload(path, newRecordFile);
      if (storageError) throw storageError;

      const { data: urlData } = supabase.storage
        .from('training-documents')
        .getPublicUrl(path);

      const { error: dbError } = await supabase
        .from('worker_training_records')
        .insert({
          worker_id: worker.id,
          title: newRecordTitle.trim(),
          document_type: newRecordType,
          document_url: urlData.publicUrl,
          uploaded_at: new Date().toISOString(),
        });
      if (dbError) throw dbError;

      setNewRecordTitle('');
      setNewRecordFile(null);
      setNewRecordType(DOCUMENT_TYPES[0]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await fetchTrainingRecords();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteRecord = async (record: TrainingRecord) => {
    if (!confirm(`Delete "${record.title}"?`)) return;
    try {
      const { error } = await supabase
        .from('worker_training_records')
        .delete()
        .eq('id', record.id);
      if (error) throw error;
      setTrainingRecords(prev => prev.filter(r => r.id !== record.id));
    } catch (err) {
      console.error('Error deleting training record:', err);
      alert('Failed to delete record');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit({
      ...formData,
      hourly_pay_rate: formData.hourly_pay_rate ? parseFloat(formData.hourly_pay_rate) : null,
      overtime_pay_rate: formData.overtime_pay_rate ? parseFloat(formData.overtime_pay_rate) : null,
      certifications: selectedCertifications
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const toggleCertification = (cert: string) => {
    setSelectedCertifications(prev =>
      prev.includes(cert) ? prev.filter(c => c !== cert) : [...prev, cert]
    );
  };

  const addCustomCertification = () => {
    if (customCertification.trim() && !selectedCertifications.includes(customCertification.trim())) {
      setSelectedCertifications(prev => [...prev, customCertification.trim()]);
      setCustomCertification('');
    }
  };

  const removeCertification = (cert: string) => {
    setSelectedCertifications(prev => prev.filter(c => c !== cert));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              {worker ? 'Edit Worker' : 'Add New Worker'}
            </h3>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
            <input
              type="text" name="name" required value={formData.name} onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Enter full name"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="inline h-4 w-4 mr-1" />Email Address *
              </label>
              <input
                type="email" name="email" required value={formData.email} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="worker@company.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Phone className="inline h-4 w-4 mr-1" />Phone Number
              </label>
              <input
                type="tel" name="phone" value={formData.phone} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="0400 000 000"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role *</label>
              <select
                name="role" required value={formData.role} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                {formData.role && !availableRoles.includes(formData.role) && (
                  <option value={formData.role}>{formData.role}</option>
                )}
                {availableRoles.map(r => (
                  <option key={r} value={r}>{r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                ))}
              </select>
              <div className="flex space-x-2 mt-2">
                <input
                  type="text"
                  value={customRole}
                  onChange={e => setCustomRole(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Add new role..."
                  onKeyPress={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const trimmed = customRole.trim();
                      if (trimmed) {
                        setAvailableRoles(prev => Array.from(new Set([...prev, trimmed])).sort());
                        setFormData(prev => ({ ...prev, role: trimmed }));
                        setCustomRole('');
                      }
                    }
                  }}
                />
                <button
                  type="button"
                  disabled={!customRole.trim()}
                  onClick={() => {
                    const trimmed = customRole.trim();
                    if (trimmed) {
                      setAvailableRoles(prev => Array.from(new Set([...prev, trimmed])).sort());
                      setFormData(prev => ({ ...prev, role: trimmed }));
                      setCustomRole('');
                    }
                  }}
                  className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                >
                  <Plus className="h-4 w-4" /><span>Add</span>
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <DollarSign className="inline h-4 w-4 mr-1" />Hourly Pay Rate
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">$</span>
                <input
                  type="number" name="hourly_pay_rate" step="0.01" min="0"
                  value={formData.hourly_pay_rate} onChange={handleChange}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="35.50"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Enter hourly rate in dollars (e.g., 35.50)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <DollarSign className="inline h-4 w-4 mr-1" />Overtime Pay Rate
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">$</span>
                <input
                  type="number" name="overtime_pay_rate" step="0.01" min="0"
                  value={formData.overtime_pay_rate} onChange={handleChange}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="53.25"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Overtime rate (typically 1.5x regular rate)</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline h-4 w-4 mr-1" />Start Date *
              </label>
              <input
                type="date" name="start_date" required value={formData.start_date} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Award className="inline h-4 w-4 mr-1" />White Card Number
              </label>
              <input
                type="text" name="whitecard_number" value={formData.whitecard_number} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Enter white card number"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Timesheet PIN</label>
            <input
              type="text" name="pin" value={formData.pin}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                setFormData(prev => ({ ...prev, pin: value }));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="4-digit PIN for timesheets"
              maxLength={4}
            />
            <p className="text-xs text-gray-500 mt-1">Optional 4-digit PIN for timesheet entry authentication</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Award className="inline h-4 w-4 mr-1" />Certifications
            </label>
            {selectedCertifications.length > 0 && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-blue-900 mb-2">Selected Certifications ({selectedCertifications.length}):</p>
                <div className="flex flex-wrap gap-2">
                  {selectedCertifications.map((cert, i) => (
                    <span key={i} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs flex items-center space-x-1">
                      <span>{cert}</span>
                      <button type="button" onClick={() => removeCertification(cert)} className="text-blue-600 hover:text-blue-800">
                        <Minus className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-3">
                Available Certifications from Database ({availableCertifications.length} available):
              </p>
              {loadingCertifications ? (
                <div className="flex items-center justify-center p-8 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span className="ml-2 text-sm text-gray-600">Loading certifications...</span>
                </div>
              ) : (
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                  {availableCertifications.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {availableCertifications.map((cert, i) => (
                        <label key={i} className="flex items-center space-x-2 text-sm cursor-pointer hover:bg-white rounded p-1">
                          <input
                            type="checkbox"
                            checked={selectedCertifications.includes(cert)}
                            onChange={() => toggleCertification(cert)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-gray-700">{cert}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500">No certifications found in database.</p>
                      <p className="text-xs text-gray-400 mt-1">Add custom certifications below to get started.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex space-x-2">
              <input
                type="text" value={customCertification}
                onChange={(e) => setCustomCertification(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Add custom certification..."
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomCertification())}
              />
              <button
                type="button" onClick={addCustomCertification} disabled={!customCertification.trim()}
                className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
              >
                <Plus className="h-4 w-4" /><span>Add</span>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Select from existing certifications above or add new certifications specific to this worker
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              name="status" value={formData.status} onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-yellow-900">Requires Health Monitoring & Fit Testing</label>
                <p className="text-xs text-yellow-700 mt-1">
                  Check this box for workers who handle asbestos or other hazardous materials requiring regular health monitoring
                </p>
              </div>
              <input
                type="checkbox" name="requires_health_monitoring"
                checked={formData.requires_health_monitoring}
                onChange={(e) => setFormData(prev => ({ ...prev, requires_health_monitoring: e.target.checked }))}
                className="rounded border-yellow-300 text-yellow-600 focus:ring-yellow-500 h-5 w-5"
              />
            </div>
            {formData.requires_health_monitoring && (
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-yellow-200">
                <div>
                  <label className="text-sm font-medium text-yellow-900">Fit Testing N/A</label>
                  <p className="text-xs text-yellow-700 mt-1">
                    Check if this worker requires health monitoring but fit testing does not apply
                  </p>
                </div>
                <input
                  type="checkbox" name="fit_testing_not_applicable"
                  checked={formData.fit_testing_not_applicable}
                  onChange={(e) => setFormData(prev => ({ ...prev, fit_testing_not_applicable: e.target.checked }))}
                  className="rounded border-yellow-300 text-yellow-600 focus:ring-yellow-500 h-5 w-5"
                />
              </div>
            )}
          </div>

          {/* Training Records — only visible when editing an existing worker */}
          {worker?.id && (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
                <FileText className="h-4 w-4 text-gray-500" />
                <h4 className="text-sm font-semibold text-gray-800">Training Records</h4>
                {trainingRecords.length > 0 && (
                  <span className="ml-auto text-xs bg-gray-200 text-gray-600 rounded-full px-2 py-0.5 font-medium">
                    {trainingRecords.length}
                  </span>
                )}
              </div>

              <div className="p-4 space-y-4">
                {/* Upload new record */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Upload New Record</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="Document title (e.g. First Aid Certificate)"
                      value={newRecordTitle}
                      onChange={e => setNewRecordTitle(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
                    />
                    <select
                      value={newRecordType}
                      onChange={e => setNewRecordType(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
                    >
                      {DOCUMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex-1 flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-white transition-colors text-sm text-gray-500">
                      <Upload className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{newRecordFile ? newRecordFile.name : 'Choose PDF or JPG…'}</span>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg"
                        className="hidden"
                        onChange={e => setNewRecordFile(e.target.files?.[0] || null)}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={handleUploadRecord}
                      disabled={uploading || !newRecordTitle.trim() || !newRecordFile}
                      className="flex items-center gap-1.5 px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                    >
                      {uploading ? <Loader className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      {uploading ? 'Uploading…' : 'Upload'}
                    </button>
                  </div>
                  {uploadError && (
                    <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{uploadError}</p>
                  )}
                </div>

                {/* Existing records list */}
                {loadingRecords ? (
                  <div className="flex items-center justify-center py-6 text-gray-400 text-sm gap-2">
                    <Loader className="h-4 w-4 animate-spin" /> Loading records…
                  </div>
                ) : trainingRecords.length === 0 ? (
                  <p className="text-center text-sm text-gray-400 py-4">No training records uploaded yet.</p>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {trainingRecords.map(record => (
                      <div key={record.id} className="flex items-center gap-3 py-3">
                        <div className="h-8 w-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FileText className="h-4 w-4 text-gray-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{record.title}</p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {new Date(record.uploaded_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </p>
                        </div>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${TYPE_COLORS[record.document_type] || TYPE_COLORS['Other']}`}>
                          {record.document_type}
                        </span>
                        <a
                          href={record.document_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                          title="Open document"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                        <button
                          type="button"
                          onClick={() => handleDeleteRecord(record)}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                          title="Delete record"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit" disabled={loading}
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Saving...' : (worker ? 'Update Worker' : 'Create Worker')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
